import React from 'react';
import { Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ControlTowerQueryParams } from '@/services/manufacturingControlTower.service';
import { useControlTowerSummary } from '@/hooks/useManufacturingControlTower';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';

interface Props {
  period: 'today' | 'week' | 'month';
  filters?: ControlTowerQueryParams;
  refreshInterval?: number;
  tvMode?: boolean;
}

const ExecutiveHealthRibbon: React.FC<Props> = ({ period, filters, refreshInterval, tvMode }) => {
  const navigate = useNavigate();
  const params: ControlTowerQueryParams = { ...filters, period };
  const { data, isLoading } = useControlTowerSummary(params, refreshInterval);
  const h = (data as { health?: Record<string, number> })?.health;

  const scroll = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const output =
    period === 'today' ? h?.outputToday : period === 'week' ? h?.outputWeek : h?.outputMonth;

  const tiles = [
    {
      title: 'Active WOs',
      count: h?.activeWorkOrders ?? 0,
      urgency: 'neutral' as const,
      onClick: () => scroll('execution'),
    },
    {
      title: 'Delayed',
      count: h?.delayedWorkOrders ?? 0,
      urgency: (h?.delayedWorkOrders ?? 0) > 0 ? ('critical' as const) : ('neutral' as const),
      onClick: () => scroll('execution'),
    },
    {
      title: 'Inspection pending',
      count: h?.inspectionPending ?? 0,
      urgency: (h?.inspectionPending ?? 0) > 0 ? ('attention' as const) : ('neutral' as const),
      onClick: () => scroll('inspections'),
    },
    {
      title: 'PR approved',
      count: h?.productionRequestsApproved ?? 0,
      urgency: 'neutral' as const,
      onClick: () => scroll('production-requests'),
    },
    {
      title: 'PR in production',
      count: h?.productionRequestsInProduction ?? 0,
      urgency: 'neutral' as const,
      onClick: () => scroll('production-requests'),
    },
    {
      title: 'Units in production',
      count: h?.unitsInProduction ?? 0,
      urgency: 'neutral' as const,
      onClick: () => scroll('fabrication'),
    },
    {
      title: 'Blocked ops',
      count: h?.blockedOperations ?? 0,
      urgency: (h?.blockedOperations ?? 0) > 0 ? ('attention' as const) : ('neutral' as const),
      onClick: () => scroll('bottlenecks'),
    },
    {
      title: 'Ready for delivery',
      count: h?.readyForDelivery ?? 0,
      urgency: 'neutral' as const,
      onClick: () => scroll('delivery'),
    },
    {
      title: 'Outsource pending',
      count: h?.outsourcedPending ?? 0,
      urgency: (h?.outsourcedPending ?? 0) > 0 ? ('attention' as const) : ('neutral' as const),
      onClick: () => scroll('outsourced'),
    },
    {
      title: 'Critical alerts',
      count: h?.criticalAlerts ?? 0,
      urgency: (h?.criticalAlerts ?? 0) > 0 ? ('critical' as const) : ('neutral' as const),
      onClick: () => scroll('alerts'),
    },
    {
      title: period === 'today' ? 'Output today' : period === 'week' ? 'Output week' : 'Output month',
      count: output ?? 0,
      urgency: 'neutral' as const,
      onClick: () => navigate('/mfg/control-tower?tab=analytics'),
    },
  ];

  return (
    <ControlTowerMetricStrip zone="metricsOrders" title="Plant health">
      {tiles.map((t) => (
        <Col xs={12} sm={8} md={6} lg={tvMode ? 6 : 4} key={t.title}>
          <ControlTowerMetricTile
            title={t.title}
            count={isLoading ? 0 : t.count}
            urgency={t.urgency}
            footerLink={{ label: 'View', onClick: t.onClick }}
          />
        </Col>
      ))}
    </ControlTowerMetricStrip>
  );
};

export default ExecutiveHealthRibbon;
